import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigurationStep } from '~/app/compositions/sidebar/configuration-step/configuration-step';
import { OptionsHeader } from '~/app/compositions/sidebar/options-header';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { useMoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { TechPackagesCompare } from '~/app/pages/configurator/components/tech-packages-compare/components/tech-packages-compare';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { ExtendedSelectionGroupWrapper } from '~/components/selection-card-new/extended-selection-group';
import { hasTeaserText } from '~/components/selection-card-new/selection-card';
import { useActiveCard } from '~/components/slider-panel';
import {
	ConfiguredCarCatalogImage,
	ConfiguredCarFeatureFragment,
	ConfiguredCarPackage,
} from '~/graphql/__generated__/graphql';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useImages } from '~/graphql/hooks/use-images';
import { useSteps } from '~/graphql/hooks/use-steps';
import { CatalogItem } from '~/graphql/mapper';
import {
	ContentOrPackage,
	FeatureCategory,
	FeatureOrPackage,
	SelectableOption,
	SelectionCardItem,
} from '~/graphql/types';
import { TECHNOLOGY_PACKAGES } from '~/utils/family-constants';

export default function EquipmentPackages() {
	const { i18n } = useI18n();
	const { mediaByProfileId } = useImages();
	const { selectCard } = useActiveCard();
	const { openMoreInformationDialog } = useMoreInformationDialog();
	const { config } = useMarketConfig();
	const { feature } = useVATConfig();
	const {
		isLoading,
		loadingFlags,
		isItemLoading,
		isItemChecked,
		catalog: { accessoriesExclusive },
	} = useConfigurator();
	const { getName } = useDebugMode();
	const [imageUrls, setImageUrls] = useState<(string[] | null | undefined)[] | undefined>([]);
	const [media, setMedia] = useState<Array<Array<ConfiguredCarCatalogImage | null | undefined>>>([]);

	const equipmentPackages = useMemo(() => {
		// Get the tech packages from the catalog
		if (!accessoriesExclusive) return;
		const _families = accessoriesExclusive.flatMap((group) => group);
		const _items = _families.flatMap((f) => f.families.flatMap((itm) => itm.items));
		return _items.filter((item) => item.family.id === TECHNOLOGY_PACKAGES);
	}, [accessoriesExclusive]);
	const { step, total } = useSteps('equipmentPackages');
	const trackSelectionClickEvent = useCallback(
		(
			eventName: string,
			label: string,
			item: FeatureOrPackage,
			elementName: string,
			value: string,
			selected?: boolean,
			eventAction = EventAction.InternalLink,
		) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction,
				eventName,
				label,
				elementName,
				value,
				category: 'equipmentPackages',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected,
				item,
			});
		},
		[step, total],
	);

	const items = useMemo(() => {
		if (!equipmentPackages) return [];
		return equipmentPackages.map((packageItem, index): SelectionCardItem => {
			return {
				name: getName(packageItem) ?? packageItem.name,
				description: hasTeaserText((packageItem.__item as ConfiguredCarFeatureFragment).texts?.teaserText)
					? (packageItem.__item as FeatureOrPackage).texts?.teaserText
					: null,
				key: packageItem.pr7,
				loading: isItemLoading(packageItem),
				status: packageItem.status,
				content:
					(packageItem.__item as FeatureOrPackage).__typename === 'ConfiguredCarPackage'
						? (packageItem.__item as ConfiguredCarPackage).content
						: undefined,
				imgUrl: packageItem?.galleryImages[0] || '',
				galleryImageUrls: packageItem?.galleryImages || [],
				galleryMedia: packageItem?.galleryMedia || [],
				selected: isItemChecked(packageItem),
				readonly: loadingFlags.checkingConflicts || !packageItem.status.available,
				onSelect: () =>
					selectCard(index, packageItem as CatalogItem, () =>
						trackSelectionClickEvent(
							'click to change configuration',
							packageItem.name || '',
							packageItem.__item as FeatureOrPackage,
							'card',
							'selection feature app: equipmentPackages: all',
							isItemChecked(packageItem),
						),
					),
				standard: packageItem.status.standard,
				price: config.showPrices
					? {
							totalPrice: packageItem.totalPrice,
							feature,
						}
					: undefined,

				textLink: {
					label: i18n('ngc.actions.moreInfo'),
					onTextLinkClick: () => {
						openMoreInformationDialog(
							packageItem as SelectableOption<FeatureCategory>,
							packageItem.__item as ContentOrPackage,
							() =>
								trackSelectionClickEvent(
									'click to change configuration',
									packageItem.name || '',
									packageItem.__item as FeatureOrPackage,
									'button',
									'details layer',
									isItemChecked(packageItem),
								),
							step,
							total,
							'equipmentPackages',
						);
					},
				},
				disclaimerContent: (packageItem.__item as FeatureOrPackage).texts?.disclaimerText || undefined,
				disclaimerHeadline: (packageItem.__item as FeatureOrPackage).texts?.disclaimerHeadline || undefined,
				disclaimerStartDate: (packageItem.__item as FeatureOrPackage).texts?.disclaimerStartDate || undefined,
				disclaimerEndDate: (packageItem.__item as FeatureOrPackage).texts?.disclaimerEndDate || undefined,
				onDescriptionToggle: (showDescription: boolean) =>
					trackSelectionClickEvent(
						`click to ${showDescription ? 'close' : 'open'} details accordion`,
						showDescription ? i18n('ngc.actions.hideDetails') : i18n('ngc.actions.showDetails'),
						packageItem.__item as FeatureOrPackage,
						'text link',
						'details accordion',
						undefined,
						EventAction.Content,
					),
			};
		});
	}, [
		equipmentPackages,
		getName,
		isItemLoading,
		isItemChecked,
		loadingFlags.checkingConflicts,
		config.showPrices,
		config.hideMonthlyRates,
		feature,
		i18n,
		selectCard,
		trackSelectionClickEvent,
		openMoreInformationDialog,
		step,
		total,
	]);

	useEffect(() => {
		const selectedItems = items?.filter((a) => a.selected);
		const images = selectedItems?.map((a) => a.galleryImageUrls) as string[][];
		const newImages = images?.filter((a) => {
			return !imageUrls?.find((b) => {
				return JSON.stringify(b) === JSON.stringify(a);
			});
		});

		const structuredMedia: Array<Array<ConfiguredCarCatalogImage | null | undefined>> = [];

		selectedItems?.forEach((itm) => {
			if (Array.isArray(itm.galleryMedia)) {
				structuredMedia.push(itm.galleryMedia);
			} else {
				structuredMedia.push([itm.galleryMedia]);
			}
		});

		setMedia(structuredMedia);

		const buffer = structuredClone(imageUrls!).filter((a) =>
			images.find((b) => JSON.stringify(b) === JSON.stringify(a)),
		);
		if (newImages) {
			setImageUrls([...structuredClone(newImages), ...structuredClone(buffer)]);
		}
	}, [JSON.stringify(imageUrls), JSON.stringify(items)]);

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && total) {
				appTrackingService.selectionStepImpression({
					node,
					value: 'equipmentPackages',
					elementCurrentStep: step,
					elementTotalSteps: total,
				});
			}
		},
		[isLoading],
	);

	const categoryDisclaimer = config.categoryDisclaimers?.find((d) => d.step === 'equipmentPackages')?.disclaimer;

	if (!equipmentPackages) return null;

	return (
		<ConfigurationStep
			ref={impressionTrackingRef}
			hide={equipmentPackages?.length === 0}
			id={'equipment-package'}
			media={
				media && media.length > 0
					? [...media, [mediaByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD]]]
					: [[mediaByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD]]]
			}
			headline={
				<OptionsHeader
					id={'equipment-package'}
					title={i18n('ngc.equipment.equipmentPackages')}
					step={step}
					total={total}
				/>
			}
			disclaimer={categoryDisclaimer}>
			{equipmentPackages?.length > 1 && <TechPackagesCompare items={items} />}

			<ExtendedSelectionGroupWrapper type="multi-select" items={items} />
		</ConfigurationStep>
	);
}
